import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';
import { FormMode, FormProperty } from 'src/app/core/models/form.models';
import { Observable, map, tap } from 'rxjs';

import { AppState } from 'src/app/core/state/app.state';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
import { ExportTemplate } from 'src/app/core/models/core.models';
import { ExportTemplateApiService } from 'src/app/api/services/export-template-api.service';
import { FormsService } from 'src/app/core/services/forms.service';
import { GeneralDynamicFormComponent } from 'src/app/shared/components/general-dynamic-form/general-dynamic-form.component';
import { LoggerService } from '@services/logger.service';
import { NomOutputFormat } from 'src/app/core/constants';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DynamicFormComponent } from '../../../../shared/components/dynamic-form/dynamic-form.component';

@Component({
  selector: 'app-export-templates-editor',
  templateUrl: './export-templates-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './export-templates-editor.component.scss',
  imports: [
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class ExportTemplatesEditorComponent extends GeneralDynamicFormComponent {
  @Input() exportTemplate: ExportTemplate | null = null;
  @Input() companyId: string | null = null;

  // Optional channel to bubble important changes (like title/name) to parent popup
  @Output() componentDataChange = new EventEmitter<{
    type: string;
    value: any;
  }>();

  public activeFormProperties: FormProperty[] = [];

  public formProperties: FormProperty[] = [
    {
      propertyName: 'output_format_id',
      propertyLabel: this.translateService.instant(
        'export-templates.label.format',
      ),
      controlType: 'select',
      validators: [Validators.required],
      valueOptions: [
        {
          value: NomOutputFormat.DOCX,
          name: this.translateService.instant('export-templates.format.docx'),
        },
        {
          value: NomOutputFormat.HTML,
          name: this.translateService.instant('export-templates.format.html'),
        },
      ],

      formChanger: true,
    },
    {
      propertyName: 'name',
      propertyLabel: this.translateService.instant(
        'export-templates.label.name',
      ),
      controlType: 'text',
      validators: [Validators.required],
      additionalConfig: {
        placeholder: this.translateService.instant(
          'export-templates.placeholder.name',
        ),
      },
    },

    {
      propertyName: 'description',
      propertyLabel: this.translateService.instant(
        'export-templates.label.description',
      ),
      controlType: 'textarea',
      validators: [],
      additionalConfig: {
        placeholder: this.translateService.instant(
          'export-templates.placeholder.description',
        ),
      },
    },
    {
      // File upload for the actual template file
      propertyName: 'file_path',
      propertyLabel: this.translateService.instant(
        'export-templates.label.file',
      ),
      controlType: 'file-upload',
      validateBy: (formGroup) => {
        const outputFormat = formGroup.get('output_format_id')
          ?.value as NomOutputFormat;

        if (outputFormat === NomOutputFormat.DOCX) {
          return [Validators.required];
        }
        return null;
      },
      additionalConfig: {
        fileType:
          'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        maxFileSizeMb: 20,
      },

      shouldDisplay: (formGroup) => {
        // Show only if output format is selected
        const outputFormat = formGroup.get('output_format_id')
          ?.value as NomOutputFormat;
        return outputFormat === NomOutputFormat.DOCX;
      },
    },

    {
      propertyName: 'html_template_file_path',
      propertyLabel: this.translateService.instant(
        'export-templates.label.html-template-file-path',
      ),
      controlType: 'select',
      validators: [Validators.required],
      validateBy: (formGroup) => {
        const outputFormat = formGroup.get('output_format_id')
          ?.value as NomOutputFormat;

        if (outputFormat === NomOutputFormat.HTML) {
          return [Validators.required];
        }
        return null;
      },
      valueOptionsAsync:
        this.exportTemplateApi.getCompanyExportTemplateHtmlOptions(),

      shouldDisplay: (formGroup) => {
        // Show only if output format is selected
        const outputFormat = formGroup.get('output_format_id')
          ?.value as NomOutputFormat;
        return outputFormat === NomOutputFormat.HTML;
      },

      formChanger: true,
    },
    {
      propertyName: 'language_id',
      propertyLabel: this.translateService.instant(
        'export-templates.label.language',
      ),
      controlType: 'select',
      validators: [Validators.required],
      additionalConfig: {
        placeholder: this.translateService.instant(
          'export-templates.select.select-language',
        ),
      },
      valueOptionsAsync: this.nomService.fetchLanguages().pipe(
        map((languages) => {
          let options = this.formService.transformForSelect(
            languages,
            (lang) => lang.id,
            (lang) => lang.name,
            null,
          );
          return options;
        }),
      ),
    },

    {
      propertyName: 'companies',
      propertyLabel: this.translateService.instant(
        'editor-user.label.available-in-companies',
      ),
      controlType: 'tag-input',
      additionalConfig: {
        valueOptionsAsync: this.companiesSuggestions(),
        displayBy: 'text',
        dropdownDisplay: 'text',
        placeholder: this.translateService.instant(
          'editor-user.label.available-in-companies',
        ),
        // placeholder: 'Select companies',
      },
    },

    // {
    //   propertyName: 'is_default',
    //   propertyLabel: this.translateService.instant(
    //     'export-templates.label.is-default',
    //   ),
    //   controlType: 'checkbox',
    //   additionalConfig: {
    //     // optional: layout config for checkbox
    //   },
    // },
  ];

  constructor(
    protected store: Store<AppState>,
    protected formService: FormsService,
    protected translateService: TranslateService,
    protected logger: LoggerService,
    protected exportTemplateApi: ExportTemplateApiService,
    protected nomService: NomenclatureService,
    protected utilsDataObjectApi: UtilsDataObjectApiService,
  ) {
    super(formService, store, logger);
  }

  ngOnInit(): void {
    // Decide form mode
    this.formMode = this.exportTemplate ? FormMode.Edit : FormMode.Create;

    super.ngOnInit();

    if (this.exportTemplate) {
      // Populate form with existing template values
      const inputTemplate = {
        ...this.exportTemplate,
        output_format_id: this.exportTemplate.output_format.id,
        language_id: this.exportTemplate.language.id,
        html_template_file_path: this.exportTemplate.file_path,
        companies: this.exportTemplate.companies.map((company) => company.id),
      };

      this.formService.populateFormValues(this.formGroup, inputTemplate);
    } else {
      // In create mode, expose the form to parent if needed
      this.formGroupChange.emit(this.formGroup);
    }
    if (this.companyId) {
      this.formGroup.get('companies').setValue([this.companyId]);
    }

    // Emit name changes upwards (similar to title in workspace settings)
    this.formGroup
      .get('name')
      ?.valueChanges.pipe(
        tap((name) =>
          this.componentDataChange.emit({
            type: 'name',
            value: name,
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  companiesSuggestions(): Observable<any> {
    return this.utilsDataObjectApi.getCompanies().pipe(
      map((companies) =>
        companies.map((company) => ({
          text: company.name,
          id: company.id,
        })),
      ),
    );
  }
}
